import React, { FC, JSX, MouseEvent } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { ActivityIcon, ActivityIconDark, ManagementIcon, ManagementIconDark, MonitoringIcon, MonitoringIconDark, OverviewIcon, SettingsIcon, CropzoneBoardIcon, CropzoneBoardIconDark, InfoCircle } from '@components/icons';
import { ActiveTabs } from '@layout/sidebar';
import { useQueryOrg } from '@services/useQueryOrg';
import { uiStore } from '@stores/UiStore';
import { useRouter } from 'next/navigation';
import { isPresent } from '@farmai-shared/LFUtils';
import { sendGaEvent } from '@services/utils';
import { PlanningIconDark } from '@components/icons/PlanningIconDark';
import { PlanningIcon } from '@components/icons/PlanningIcon';
import { TraceabilityIconDark } from '@components/icons/TraceabilityIconDark';
import { TraceabilityIcon } from '@components/icons/TraceabilityIcon';
export interface ISidebarElement {
  id: ActiveTabs;
  icon: (active: boolean) => JSX.Element;
  url: string;
}
export const sidebarElements: ISidebarElement[] = [{
  id: 'overview',
  icon: active => <OverviewIcon active={active} />,
  url: '/overview'
}, {
  id: 'management',
  icon: active => active ? <ManagementIconDark /> : <ManagementIcon />,
  url: '/management'
}, {
  id: 'cropzoneBoard',
  icon: active => active ? <CropzoneBoardIconDark /> : <CropzoneBoardIcon />,
  url: '/cropzones'
}, {
  id: 'activities',
  icon: active => active ? <ActivityIconDark /> : <ActivityIcon />,
  url: '/activity'
}, {
  id: 'planning',
  icon: active => active ? <PlanningIconDark /> : <PlanningIcon />,
  url: '/planning'
}, {
  id: 'traceability',
  icon: active => active ? <TraceabilityIconDark /> : <TraceabilityIcon />,
  url: '/traceability'
}, {
  id: 'supplyChain',
  icon: active => active ? <PlanningIconDark /> : <PlanningIcon />,
  url: '/supply-chain'
}, {
  id: 'monitoring',
  icon: active => active ? <MonitoringIconDark width="2.5rem" /> : <MonitoringIcon width="2.5rem" />,
  url: '/monitoring'
}, {
  id: 'settings',
  icon: active => <SettingsIcon active={active} />,
  url: '/settings'
}];
export const SidebarElement: FC<{
  collapsed: boolean;
  element: Omit<ISidebarElement, 'url'>;
  active?: boolean;
  customStyles?: string;
}> = observer(({
  collapsed,
  element,
  active,
  customStyles
}) => {
  const {
    t
  } = useTranslation();
  const router = useRouter();
  const {
    data: unfinishedCropzones
  } = useQueryOrg({
    queryKey: ['drafts', 'cropzone count'],
    queryFn: () => uiStore.currentOrganization!.fetchCropzones({
      pending: true
    }),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const onIconClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    router.push('/cropzones/drafts');
    sendGaEvent('view_draft_cropzones', {
      event_category: 'quick_cropzone',
      action: 'click',
      navigation_source: 'icon'
    });
  };
  return <div className={`
        ${customStyles ? customStyles : 'absolute top-0 bottom-0 left-0 my-4 ml-8'}
        flex items-center justify-start
        ${collapsed ? 'md:ml-0 md:justify-center' : ''}
      `}>
      <div className={`
          w-12 p-2
          hover:text-secondary-300
          md:w-16
        `} data-tooltip-place="right" data-tooltip-id="main-tooltip" data-tooltip-content={t(`sidebar_${element.id}`)} data-tooltip-hidden={!collapsed} data-tooltip-class-name="z-50">
        {element.icon(active || false)}
      </div>
      <p className={`
          ml-4 font-semibold
          ${collapsed ? 'md:hidden' : ''}
        `}>
        {t(`sidebar_${element.id}`)}
      </p>
      {element?.id === 'cropzoneBoard' && isPresent(unfinishedCropzones) && <button type="button" onClick={onIconClick} className={`
            ml-5 rounded-2xl p-2
            hover:bg-gray-200
          `}>
          <InfoCircle data-tooltip-place="top" data-tooltip-id="main-tooltip" data-tooltip-content={t('sidebar_unfinished_cropzones', {
        num_unfinished_cropzones: unfinishedCropzones.length
      })} data-tooltip-class-name="z-50" className={`
              hidden h-10 w-10 fill-none stroke-warning-500 outline-hidden
              md:inline-block
            `} />
        </button>}
    </div>;
});